/**
 * The differences reported by a finished comparison, as links.
 *
 * Clicking one asks the local agent to move the open AutoCAD view. It does not
 * start a chat turn.
 */
import { useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import { agentClient } from "../../lib/agentClient";
import { cadChangeLabel, cadChangeViewRequest, type CadChange, type CadReview } from "../../lib/cadChanges";

export function CadChangeLinks({
  review,
  sessionId,
}: {
  review: CadReview;
  sessionId: string | null;
}) {
  const { t } = useLocale();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { changes, annotationStatus } = review;
  if (changes.length === 0) return null;

  async function zoom(change: CadChange): Promise<void> {
    const request = cadChangeViewRequest(review, sessionId, change);
    if (!request) {
      setError(t("cad.zoomWrongDrawing"));
      return;
    }
    setBusyId(change.id);
    setError(null);
    try {
      const response = await agentClient.fetch("/autocad/show-change", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(request),
      });
      if (!response.ok) setError(t(response.status === 409 ? "cad.zoomWrongDrawing" : "cad.zoomFailed"));
    } catch {
      setError(t("cad.zoomFailed"));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="cad-changes">
      <span className="cad-changes-label">{t("cad.changes")}</span>
      {annotationStatus ? <span>{t(`cad.annotations.${annotationStatus}`)}</span> : null}
      {changes.map((change) => (
        <button
          key={change.id}
          type="button"
          className="cad-change"
          disabled={busyId !== null}
          onClick={() => void zoom(change)}
        >
          {busyId === change.id ? t("cad.zooming") : cadChangeLabel(change, t)}
        </button>
      ))}
      {error ? <span className="cad-change-error">{error}</span> : null}
    </div>
  );
}
